import React, { useCallback, useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { TransactionRow } from "@/components/TransactionList";
import { Loader2, Search, Inbox, AlertTriangle } from "lucide-react";

const PAGE = 30;

export default function ActivityView({ onSelect, refreshKey }) {
  const { t } = useApp();
  const [dir, setDir] = useState("all");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [retryN, setRetryN] = useState(0);

  const fetchPage = useCallback(async (before) => {
    const params = { limit: PAGE };
    if (dir !== "all") params.direction = dir;
    if (q.trim()) params.q = q.trim();
    if (before) params.before = before;
    const r = await api.get("/transactions", { params });
    return r.data;
  }, [dir, q]);

  useEffect(() => {
    let alive = true;
    setState("loading");
    const h = setTimeout(async () => {
      try {
        const data = await fetchPage();
        if (!alive) return;
        setItems(data); setMore(data.length === PAGE); setState("ready");
      } catch { if (alive) setState("error"); }
    }, q ? 300 : 0);
    return () => { alive = false; clearTimeout(h); };
  }, [fetchPage, q, refreshKey, retryN]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchPage(items[items.length - 1]?.created_at);
      setItems((x) => [...x, ...data]); setMore(data.length === PAGE);
    } catch {} finally { setLoadingMore(false); }
  };

  const chips = [["all", t.filterAll], ["in", t.filterIn], ["out", t.filterOut]];

  return (
    <div data-testid="activity-view" className="pt-4">
      <h2 className="text-xl font-black mb-3">{t.navActivity}</h2>
      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground" />
        <Input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)} maxLength={40}
          placeholder={t.searchActivity} className="h-11 rounded-2xl ps-9" />
      </div>
      <div className="flex gap-2 mb-4">
        {chips.map(([k, label]) => (
          <button key={k} data-testid={`activity-filter-${k}`} onClick={() => setDir(k)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${dir === k ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/80 hover:bg-muted/70"}`}>
            {label}
          </button>
        ))}
      </div>
      {state === "loading" && (
        <div className="space-y-2.5">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-[68px] rounded-2xl bg-muted/60 animate-pulse" />)}</div>
      )}
      {state === "error" && (
        <div className="py-12 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <p className="font-semibold">{t.loadError}</p>
          <button onClick={() => setRetryN((n) => n + 1)} className="mt-3 text-sm font-bold text-primary" data-testid="activity-retry">{t.retry}</button>
        </div>
      )}
      {state === "ready" && items.length === 0 && (
        <div className="py-14 text-center">
          <div className="w-16 h-16 rounded-full bg-muted grid place-items-center mx-auto mb-4"><Inbox className="w-7 h-7 text-muted-foreground" /></div>
          <p className="font-semibold">{q || dir !== "all" ? t.noResults : t.noActivity}</p>
          {!q && dir === "all" && <p className="text-sm text-muted-foreground mt-1">{t.noActivitySub}</p>}
        </div>
      )}
      {state === "ready" && items.length > 0 && (
        <div className="space-y-2.5">
          {items.map((tx) => <TransactionRow key={tx.tx_id} tx={tx} onClick={() => onSelect(tx)} />)}
          {more && (
            <button data-testid="activity-load-more" onClick={loadMore} disabled={loadingMore}
              className="w-full h-11 rounded-2xl border border-border font-semibold text-sm hover:bg-muted/50">
              {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t.loadMore}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
